import React, { useEffect, useState } from 'react';
import planchetteIntelService from '../../services/planchetteIntelService';

const EntityDetail = ({ entityId, onShowGraph, onBack }) => {
  const [entity, setEntity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enriching, setEnriching] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [watchCreating, setWatchCreating] = useState(false);

  useEffect(() => {
    loadEntity();
    loadJobs();
  }, [entityId]);

  const loadEntity = async () => {
    try {
      setLoading(true);
      const data = await planchetteIntelService.getEntity(entityId);
      setEntity(data);
    } catch (err) {
      console.error('Failed to load entity:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadJobs = async () => {
    try {
      const data = await planchetteIntelService.getEnrichmentJobs({ entity_id: entityId, limit: 10 });
      setJobs(data.items);
    } catch (err) {
      console.error('Failed to load jobs:', err);
    }
  };

  const handleDownloadDossier = async () => {
    try {
      setDownloading(true);
      await planchetteIntelService.downloadReport(entityId);
    } catch (err) {
      console.error('Failed to download dossier:', err);
    } finally {
      setDownloading(false);
    }
  };

  const handleEnrich = async () => {
    try {
      setEnriching(true);
      await planchetteIntelService.enrich({ entity_id: entityId });
      setTimeout(() => {
        loadEntity();
        loadJobs();
        setEnriching(false);
      }, 3000);
    } catch (err) {
      console.error('Enrichment failed:', err);
      setEnriching(false);
    }
  };

  const handleCreateWatch = async () => {
    try {
      setWatchCreating(true);
      await planchetteIntelService.createWatch({
        entity_id: entityId,
        watch_types: ['all'],
        channels: ['virgo'],
        check_interval_hours: 24,
      });
      setWatchCreating(false);
    } catch (err) {
      console.error('Watch creation failed:', err);
      setWatchCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-virgo-teal-400"></div>
      </div>
    );
  }

  if (!entity) {
    return <p className="text-center text-gray-500 py-20">Entity not found.</p>;
  }

  const typeColors = {
    person: 'blue',
    organization: 'purple',
    property: 'green',
    court_case: 'orange',
    vehicle: 'gray',
    aircraft: 'slate',
  };
  const color = typeColors[entity.entity_type] || 'gray';

  const groupedEdges = {};
  (entity.edges || []).forEach((edge) => {
    const key = edge.rel_type;
    if (!groupedEdges[key]) groupedEdges[key] = [];
    groupedEdges[key].push(edge);
  });

  return (
    <div className="p-6 space-y-6">
      {/* Entity Header */}
      <div className="bg-virgo-navy-800/50 backdrop-blur-sm border border-virgo-navy-600 rounded-lg p-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className={`px-3 py-1 rounded-full text-xs font-medium text-white bg-${color}-600`}>
                {entity.entity_type}
              </span>
              {entity.needs_review && (
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-500 text-black">
                  Needs Review
                </span>
              )}
              {entity.contact_id && (
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-600 text-white">
                  Linked to Contact
                </span>
              )}
            </div>
            <h2 className="text-2xl font-bold text-white">{entity.display_name}</h2>
            <p className="text-sm text-gray-400 mt-1">
              Confidence: {(entity.confidence * 100).toFixed(0)}%
              {entity.first_seen && <span className="ml-3">First seen: {new Date(entity.first_seen).toLocaleDateString()}</span>}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => onShowGraph(entityId)}
              className="px-4 py-2 bg-virgo-navy-700 border border-virgo-navy-500 rounded-lg text-sm text-white hover:border-virgo-teal-500 transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
              View Graph
            </button>
            <button
              onClick={handleEnrich}
              disabled={enriching}
              className="px-4 py-2 bg-virgo-teal-600 hover:bg-virgo-teal-500 disabled:opacity-50 rounded-lg text-sm text-white font-medium transition-colors flex items-center gap-2"
            >
              {enriching ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              )}
              Enrich Now
            </button>
            <button
              onClick={handleCreateWatch}
              disabled={watchCreating}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 rounded-lg text-sm text-white font-medium transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              Watch
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Properties Panel */}
        <div className="bg-virgo-navy-800/50 backdrop-blur-sm border border-virgo-navy-600 rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-300 mb-3">Properties</h3>
          {entity.data && Object.keys(entity.data).length > 0 ? (
            <div className="space-y-2">
              {Object.entries(entity.data).map(([key, value]) => {
                if (value === null || value === undefined || value === '') return null;
                const displayValue = typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value);
                return (
                  <div key={key} className="flex justify-between py-1 border-b border-virgo-navy-700">
                    <span className="text-xs text-gray-400">{key.replace(/_/g, ' ')}</span>
                    <span className="text-xs text-white text-right max-w-[60%] truncate" title={displayValue}>
                      {displayValue.length > 80 ? displayValue.slice(0, 80) + '...' : displayValue}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No properties</p>
          )}
        </div>

        {/* Relationships Panel */}
        <div className="bg-virgo-navy-800/50 backdrop-blur-sm border border-virgo-navy-600 rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-300 mb-3">
            Relationships ({entity.edges?.length || 0})
          </h3>
          {Object.keys(groupedEdges).length > 0 ? (
            <div className="space-y-4">
              {Object.entries(groupedEdges).map(([relType, edges]) => (
                <div key={relType}>
                  <p className="text-xs text-virgo-teal-400 font-medium mb-1">
                    {relType.replace(/_/g, ' ')} ({edges.length})
                  </p>
                  <div className="space-y-1 ml-3">
                    {edges.map((edge) => (
                      <div key={edge.id} className="flex items-center gap-2 text-sm">
                        <span className="text-gray-400">{edge.direction === 'outgoing' ? '→' : '←'}</span>
                        <span className="text-white">{edge.other_id?.slice(0, 8)}...</span>
                        <span className="text-xs text-gray-500">
                          ({(edge.confidence * 100).toFixed(0)}%)
                        </span>
                        {!edge.current && <span className="text-xs text-red-400">ended</span>}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No relationships. Run enrichment to discover connections.</p>
          )}
        </div>
      </div>

      {/* Sources */}
      {entity.sources?.length > 0 && (
        <div className="bg-virgo-navy-800/50 backdrop-blur-sm border border-virgo-navy-600 rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-300 mb-3">Sources</h3>
          <div className="space-y-2">
            {entity.sources.map((source, i) => (
              <div key={i} className="flex items-center justify-between py-1 border-b border-virgo-navy-700">
                <span className="text-xs text-white">{source.name}</span>
                <div className="flex items-center gap-3">
                  {source.record_id && <span className="text-xs text-gray-500">ID: {source.record_id}</span>}
                  {source.retrieved_at && <span className="text-xs text-gray-500">{new Date(source.retrieved_at).toLocaleDateString()}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Enrichment History */}
      {jobs.length > 0 && (
        <div className="bg-virgo-navy-800/50 backdrop-blur-sm border border-virgo-navy-600 rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-300 mb-3">Enrichment History</h3>
          <div className="space-y-2">
            {jobs.map((job) => (
              <div key={job.id} className="flex items-center justify-between py-2 border-b border-virgo-navy-700">
                <div className="flex items-center gap-3">
                  <span className={`w-2 h-2 rounded-full ${
                    job.status === 'completed' ? 'bg-green-400' :
                    job.status === 'running' ? 'bg-blue-400 animate-pulse' :
                    job.status === 'failed' ? 'bg-red-400' : 'bg-gray-400'
                  }`}></span>
                  <span className="text-xs text-white">{job.triggered_by}</span>
                  <span className="text-xs text-gray-500">
                    Sources: {job.sources_queried?.join(', ')}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-400">
                    +{job.new_entities} entities, +{job.new_relationships} edges
                  </span>
                  <span className="text-xs text-gray-500">
                    {job.completed_at ? new Date(job.completed_at).toLocaleString() : job.started_at ? 'Running...' : 'Pending'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default EntityDetail;
